import { Vector3 } from 'three';

import { generateQuadraticBezierPoints } from '../../../three-helpers/animation/path-interpolation';

/** Number of segments for the bezier arc path */
const ARC_SEGMENTS = 32;

/** Distance (inches) from auto-zoom position to presentation point */
const PRESENTATION_DISTANCE = 48;

export interface BezierControlPoints {
	arcStart: Vector3;
	control: Vector3;
	end: Vector3;
}

/**
 * Compute the three bezier control points for the auto-zoom arc.
 *
 * Arc start: source position (already at elevated Y).
 * Control point: XZ midpoint of source and target, Y midpoint for smooth descent.
 * Arc end: target (vectorTarget).
 */
export const buildArcControlPoints = (params: {
	source: Vector3;
	target: Vector3;
}): BezierControlPoints => {
	const arcStart = params.source.clone();
	const control = new Vector3(
		(arcStart.x + params.target.x) / 2,
		(arcStart.y + params.target.y) / 2,
		(arcStart.z + params.target.z) / 2
	);
	return { arcStart, control, end: params.target.clone() };
};

/**
 * Build the full quadratic bezier arc path as an array of points.
 */
export const buildAutoZoomArcPath = (cp: BezierControlPoints): Vector3[] => generateQuadraticBezierPoints(cp.arcStart, cp.control, cp.end, ARC_SEGMENTS);

/**
 * Compute the quadratic bezier tangent at parameter t.
 * B'(t) = 2(1-t)(P1 - P0) + 2t(P2 - P1)
 */
export const bezierTangentAt = (cp: BezierControlPoints, t: number): Vector3 => {
	const oneMinusT = 1 - t;
	const { arcStart: p0, control: p1, end: p2 } = cp;
	return new Vector3(
		(2 * oneMinusT * (p1.x - p0.x)) + (2 * t * (p2.x - p1.x)),
		(2 * oneMinusT * (p1.y - p0.y)) + (2 * t * (p2.y - p1.y)),
		(2 * oneMinusT * (p1.z - p0.z)) + (2 * t * (p2.z - p1.z))
	);
};

/**
 * Sample a quadratic bezier at parameter t.
 * B(t) = (1-t)²P0 + 2(1-t)tP1 + t²P2
 */
export const bezierPointAt = (cp: BezierControlPoints, t: number): Vector3 => {
	const oneMinusT = 1 - t;
	const { arcStart: p0, control: p1, end: p2 } = cp;
	return new Vector3(
		(oneMinusT * oneMinusT * p0.x) + (2 * oneMinusT * t * p1.x) + (t * t * p2.x),
		(oneMinusT * oneMinusT * p0.y) + (2 * oneMinusT * t * p1.y) + (t * t * p2.y),
		(oneMinusT * oneMinusT * p0.z) + (2 * oneMinusT * t * p1.z) + (t * t * p2.z)
	);
};

/**
 * Compute the presentation point: 48" from arc position toward the camera target.
 */
export const computePresentationPoint = (arcPosition: Vector3, target: Vector3): Vector3 => {
	const direction = new Vector3().subVectors(target, arcPosition)
		.normalize();
	return arcPosition.clone().add(direction.multiplyScalar(PRESENTATION_DISTANCE));
};
